import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Compass, Check, Smile, Meh, Frown, Heart, Angry } from "lucide-react";
import { toast } from "sonner";

const moods = [
  { id: "great", label: "Great", icon: Heart, color: "text-green-400", bgColor: "bg-green-500/20" },
  { id: "good", label: "Good", icon: Smile, color: "text-[#C4B5FD]", bgColor: "bg-[#8B5CF6]/20" },
  { id: "okay", label: "Okay", icon: Meh, color: "text-[#D4AF37]", bgColor: "bg-[#D4AF37]/20" },
  { id: "low", label: "Low", icon: Frown, color: "text-orange-400", bgColor: "bg-orange-500/20" },
  { id: "difficult", label: "Difficult", icon: Angry, color: "text-red-400", bgColor: "bg-red-500/20" },
];

const ReflectionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedMood, setSelectedMood] = useState(null);
  const [guidanceHelped, setGuidanceHelped] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingReflection, setExistingReflection] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);

  useEffect(() => {
    const checkExisting = async () => {
      try {
        const response = await api.get("/reflection/today");
        if (response.data && response.data.reflection_id) {
          setExistingReflection(response.data);
          setSelectedMood(response.data.mood);
          setGuidanceHelped(response.data.guidance_helped);
          setNotes(response.data.notes || "");
        }
      } catch (error) {
        console.error("Error checking existing reflection:", error);
      } finally {
        setCheckingExisting(false);
      }
    };
    checkExisting();
  }, []);

  const handleSubmit = async () => {
    if (!selectedMood) {
      toast.error("Please select how you're feeling");
      return;
    }
    if (guidanceHelped === null) {
      toast.error("Please indicate if today's guidance helped");
      return;
    }

    setLoading(true);
    try {
      await api.post("/reflection", {
        mood: selectedMood,
        guidance_helped: guidanceHelped,
        notes: notes.trim(),
      });
      toast.success(existingReflection ? "Reflection updated!" : "Reflection saved!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Error saving reflection:", error);
      toast.error("Failed to save reflection");
    } finally {
      setLoading(false);
    }
  };

  if (checkingExisting) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="reflection-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Compass className="w-4 h-4 text-[#070B14]" />
            </div>
            <span className="font-display text-xl text-white">Reflection</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Title */}
        <div className="text-center animate-fade-in-up">
          <h1 className="font-display text-3xl sm:text-4xl text-white mb-2">
            {existingReflection ? "Update Your Reflection" : "End of Day Reflection"}
          </h1>
          <p className="text-white/60 font-body">
            Take a moment to reflect on your day, {user?.first_name || user?.name}
          </p>
        </div>

        {/* Mood Selection */}
        <div className="glass-card p-6 sm:p-8 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          <h2 className="font-display text-xl text-white mb-6">How are you feeling?</h2>
          <div className="grid grid-cols-5 gap-2 sm:gap-4">
            {moods.map((mood) => {
              const Icon = mood.icon;
              const isSelected = selectedMood === mood.id;
              return (
                <button
                  key={mood.id}
                  data-testid={`mood-${mood.id}`}
                  onClick={() => setSelectedMood(mood.id)}
                  className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-xl border transition-all duration-300 ${
                    isSelected
                      ? `${mood.bgColor} border-current ${mood.color} mood-btn-selected`
                      : "border-white/10 bg-white/5 hover:bg-white/10 text-white/60"
                  }`}
                >
                  <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${isSelected ? mood.color : ""}`} />
                  <span className="text-xs sm:text-sm font-body">{mood.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Guidance Helped */}
        <div className="glass-card p-6 sm:p-8 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
          <h2 className="font-display text-xl text-white mb-6">Did today's guidance help you?</h2>
          <div className="flex gap-4">
            <button
              data-testid="guidance-helped-yes"
              onClick={() => setGuidanceHelped(true)}
              className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-xl border transition-all duration-300 ${
                guidanceHelped === true
                  ? "bg-green-500/20 border-green-400 text-green-400"
                  : "border-white/10 bg-white/5 hover:bg-white/10 text-white/60"
              }`}
            >
              {guidanceHelped === true && <Check className="w-5 h-5" />}
              <span className="font-body">Yes, it helped</span>
            </button>
            <button
              data-testid="guidance-helped-no"
              onClick={() => setGuidanceHelped(false)}
              className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-xl border transition-all duration-300 ${
                guidanceHelped === false
                  ? "bg-orange-500/20 border-orange-400 text-orange-400"
                  : "border-white/10 bg-white/5 hover:bg-white/10 text-white/60"
              }`}
            >
              {guidanceHelped === false && <Check className="w-5 h-5" />}
              <span className="font-body">Not really</span>
            </button>
          </div>
        </div>

        {/* Notes */}
        <div className="glass-card p-6 sm:p-8 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <h2 className="font-display text-xl text-white mb-4">Any thoughts to capture?</h2>
          <p className="text-white/50 text-sm font-body mb-4">Optional - write anything you'd like to remember</p>
          <Textarea
            data-testid="reflection-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What stood out today? What are you grateful for?"
            className="custom-input min-h-[120px] resize-none"
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4 animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
          <Button
            data-testid="save-reflection"
            onClick={handleSubmit}
            disabled={loading}
            className="btn-gold w-full text-lg py-6"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-[#070B14] border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : existingReflection ? (
              "Update Reflection"
            ) : (
              "Complete Reflection"
            )}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ReflectionPage;
